import { useNavigate } from "react-router-dom";
import { DollarSign, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import { ReportCard } from "../ReportCard";
import { useAuth } from "@/contexts/AuthContext";
import { useProgramYear } from "@/contexts/ProgramYearContext";
import {
  usePaymentAdjustmentConfig,
  usePracticeEligibility,
} from "@/hooks/queries/useMipsQueries";
// Payment-adjustment math lives in the shared lib (R10 extract 2026-08-03)
import { computeAdjustmentPct as calculateAdjustmentPercent } from "@/lib/payment-adjustment";

function formatCurrencyCompact(amount: number): string {
  const absAmount = Math.abs(amount);
  let formatted: string;
  if (absAmount >= 1000000) {
    formatted = `$${(absAmount / 1000000).toFixed(1)}M`;
  } else if (absAmount >= 1000) {
    formatted = `$${(absAmount / 1000).toFixed(0)}K`;
  } else {
    formatted = `$${absAmount.toFixed(0)}`;
  }
  if (amount < 0) return '-' + formatted;
  if (amount > 0) return '+' + formatted;
  return formatted;
}

function determineZone(score: number): 'penalty' | 'neutral' | 'bonus' {
  if (score <= 74) return 'penalty';
  if (score === 75) return 'neutral';
  return 'bonus';
}

interface PaymentAdjustmentCardProps {
  compositeScore?: number;
}

export function PaymentAdjustmentCard({ compositeScore = 0 }: PaymentAdjustmentCardProps) {
  const navigate = useNavigate();
  const { selectedPractice } = useAuth();
  const { programYear } = useProgramYear();

  const practiceId = selectedPractice?.id;
  const score = Math.min(100, Math.max(0, compositeScore));

  // [Data-freshness 2026-08-12] Config + eligibility come from the shared
  // React Query cache (replaces the module-level paymentDataCache): revisits
  // paint from cache instantly (placeholderData — no empty-state flash), and
  // eligibility uploads invalidate it like every other consumer.
  const configQ = usePaymentAdjustmentConfig(programYear);
  const eligQ = usePracticeEligibility(practiceId, programYear);

  const config = configQ.data ?? null;
  const medicareRevenue: number | null =
    eligQ.data?.imported && eligQ.data.practice?.allowed_charges
      ? eligQ.data.practice.allowed_charges
      : null;
  const loaded = !configQ.isPending && (!practiceId || !eligQ.isPending);

  const adjustmentPercent = config ? calculateAdjustmentPercent(Math.round(score), config.low_scenario_input, config.scaling_factor) : 0;
  const dollarAmount = medicareRevenue ? medicareRevenue * adjustmentPercent : null;
  const zone = determineZone(Math.round(score));
  const hasData = config && medicareRevenue && dollarAmount !== null;

  // Preview content showing score and projection
  const preview = (
    <div className="w-full flex items-center justify-around py-4">
      {/* Score */}
      <div className="text-center">
        <p className="text-4xl font-bold">{score.toFixed(0)}</p>
        <p className="text-xs text-muted-foreground mt-1">MIPS Score</p>
      </div>

      {/* Divider */}
      <div className="h-16 w-px bg-border" />

      {/* Projection */}
      <div className="text-center">
        {hasData ? (
          <>
            <div className="flex items-center justify-center gap-2">
              {zone === 'bonus' && <TrendingUp className="h-5 w-5 text-emerald-500" />}
              {zone === 'penalty' && <TrendingDown className="h-5 w-5 text-red-500" />}
              {zone === 'neutral' && <Minus className="h-5 w-5 text-slate-500" />}
              <p className={cn(
                "text-3xl font-bold",
                zone === 'bonus' && "text-emerald-600 dark:text-emerald-400",
                zone === 'penalty' && "text-red-600 dark:text-red-400",
                zone === 'neutral' && "text-slate-600"
              )}>
                {formatCurrencyCompact(dollarAmount)}
              </p>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Projected Impact</p>
          </>
        ) : !loaded ? (
          // Cold load: sized pulse placeholder — never claim "Configure to see"
          // while the fetch is still in flight (snappiness rule).
          <>
            <div className="mx-auto h-9 w-24 animate-pulse rounded bg-muted" />
            <p className="text-xs text-muted-foreground mt-1">Projected Impact</p>
          </>
        ) : (
          <>
            <p className="text-2xl font-semibold text-muted-foreground">—</p>
            <p className="text-xs text-muted-foreground mt-1">Configure to see</p>
          </>
        )}
      </div>
    </div>
  );

  return (
    <ReportCard
      title="MIPS Payment Adjustment"
      description="Projected Medicare payment adjustments based on your MIPS score. Compare group vs individual reporting options."
      icon={<DollarSign className="h-6 w-6" />}
      onClick={() => navigate('/insights/payment-adjustment')}
      status="available"
      preview={preview}
    />
  );
}
